import cloneDeep from 'lodash/cloneDeep';

import {
  DataTransfer,
  DataTransferCostItem,
  DataTransferItem,
  DataTransferMetricsPerType,
} from '@cast/shared-types';

import { toBig } from '../number';

export const EMPTY_DATA_ITEM: DataTransferItem = {
  cloudApiBytes: '0',
  cloudApiCost: '0',
  interRegionBytes: '0',
  interRegionCost: '0',
  interZoneBytes: '0',
  interZoneCost: '0',
  intraZoneBytes: '0',
  intraZoneCost: '0',
  internetBytes: '0',
  internetCost: '0',
};

export const sumDataTransferCost = ({
  cloudApiCost = '0.000000',
  interRegionCost = '0.000000',
  interZoneCost = '0.000000',
  intraZoneCost = '0.000000',
  internetCost = '0.000000',
}: DataTransferCostItem): number => {
  return toBig(cloudApiCost)
    .plus(interRegionCost)
    .plus(interZoneCost)
    .plus(intraZoneCost)
    .plus(internetCost)
    .toNumber();
};

export const sumDataTransferTraffic = ({
  cloudApiBytes = '0',
  interRegionBytes = '0',
  interZoneBytes = '0',
  intraZoneBytes = '0',
  internetBytes = '0',
}: DataTransferCostItem): number => {
  return toBig(cloudApiBytes)
    .plus(interRegionBytes)
    .plus(interZoneBytes)
    .plus(intraZoneBytes)
    .plus(internetBytes)
    .toNumber();
};

// prettier-ignore
export const aggregatorByTimestampFn = (acc: Record<string, DataTransfer>, type: 'egress' | 'ingress', data: DataTransferCostItem): void => {
  const _timestamp = data.timestamp ?? '';
  if (_timestamp) {
    // Add empty datapoint for later calculations
    acc[_timestamp] ??= {
      egress: cloneDeep(EMPTY_DATA_ITEM),
      ingress: cloneDeep(EMPTY_DATA_ITEM)
    };


    acc[_timestamp][type].cloudApiBytes = toBig(acc[_timestamp][type].cloudApiBytes).plus(data.cloudApiBytes ?? '0').toString();
    acc[_timestamp][type].cloudApiCost = toBig(acc[_timestamp][type].cloudApiCost).plus(data.cloudApiCost ?? '0').toFixed(6);
    acc[_timestamp][type].interRegionBytes = toBig(acc[_timestamp][type].interRegionBytes).plus(data.interRegionBytes ?? '0').toString();
    acc[_timestamp][type].interRegionCost = toBig(acc[_timestamp][type].interRegionCost).plus(data.interRegionCost ?? '0').toFixed(6);
    acc[_timestamp][type].interZoneBytes = toBig(acc[_timestamp][type].interZoneBytes).plus(data.interZoneBytes ?? '0').toString();
    acc[_timestamp][type].interZoneCost = toBig(acc[_timestamp][type].interZoneCost).plus(data.interZoneCost ?? '0').toFixed(6);
    acc[_timestamp][type].intraZoneBytes = toBig(acc[_timestamp][type].intraZoneBytes).plus(data.intraZoneBytes ?? '0').toString();
    acc[_timestamp][type].intraZoneCost = toBig(acc[_timestamp][type].intraZoneCost).plus(data.intraZoneCost ?? '0').toFixed(6);
    acc[_timestamp][type].internetBytes = toBig(acc[_timestamp][type].internetBytes).plus(data.internetBytes ?? '0').toString();
    acc[_timestamp][type].internetCost = toBig(acc[_timestamp][type].internetCost).plus(data.internetCost ?? '0').toFixed(6);
  }
};

// prettier-ignore
export const aggregatorByTypeFn = (acc: DataTransfer, type: 'egress' | 'ingress', data: DataTransferCostItem): void => {
  acc[type] ??= {
    ...cloneDeep(EMPTY_DATA_ITEM)
  };

  acc[type].cloudApiBytes = toBig(acc[type].cloudApiBytes).plus(data.cloudApiBytes ?? '0').toString();
  acc[type].cloudApiCost = toBig(acc[type].cloudApiCost).plus(data.cloudApiCost ?? '0').toFixed(6);
  acc[type].interRegionBytes = toBig(acc[type].interRegionBytes).plus(data.interRegionBytes ?? '0').toString();
  acc[type].interRegionCost = toBig(acc[type].interRegionCost).plus(data.interRegionCost ?? '0').toFixed(6);
  acc[type].interZoneBytes = toBig(acc[type].interZoneBytes).plus(data.interZoneBytes ?? '0').toString();
  acc[type].interZoneCost = toBig(acc[type].interZoneCost).plus(data.interZoneCost ?? '0').toFixed(6);
  acc[type].intraZoneBytes = toBig(acc[type].intraZoneBytes).plus(data.intraZoneBytes ?? '0').toString();
  acc[type].intraZoneCost = toBig(acc[type].intraZoneCost).plus(data.intraZoneCost ?? '0').toFixed(6);
  acc[type].internetBytes = toBig(acc[type].internetBytes).plus(data.internetBytes ?? '0').toString();
  acc[type].internetCost = toBig(acc[type].internetCost).plus(data.internetCost ?? '0').toFixed(6);
};

export const aggregateDataTransferMetrics = (
  egress?: DataTransferCostItem[],
  ingress?: DataTransferCostItem[]
): DataTransferMetricsPerType => {
  const aggregated: DataTransfer = {
    egress: cloneDeep(EMPTY_DATA_ITEM),
    ingress: cloneDeep(EMPTY_DATA_ITEM),
  };
  const egressAggregatorFn = aggregatorByTypeFn.bind(
    null,
    aggregated,
    'egress'
  );
  const ingressAggregatorFn = aggregatorByTypeFn.bind(
    null,
    aggregated,
    'ingress'
  );

  egress?.forEach(egressAggregatorFn);
  ingress?.forEach(ingressAggregatorFn);

  const egressCost = sumDataTransferCost(aggregated.egress);
  const ingressCost = sumDataTransferCost(aggregated.ingress);

  const egressTraffic = sumDataTransferTraffic(aggregated.egress);
  const ingressTraffic = sumDataTransferTraffic(aggregated.ingress);

  return {
    cost: {
      egress: egressCost,
      ingress: ingressCost,
      total: egressCost + ingressCost,
    },
    traffic: {
      egress: egressTraffic,
      ingress: ingressTraffic,
      total: egressTraffic + ingressTraffic,
    },
    ...aggregated,
  };
};
